/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable, NgZone } from '@angular/core';
import { State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { isNotNil } from '@gh/core-util';
import { filter$, skip$ } from '@gh/rx/operators';
import {
  assign,
  countBy,
  differenceBy,
  filter,
  flatten,
  intersectionBy,
  isEmpty,
  isEqual,
  keyBy,
  padStart,
  toPairs,
} from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { GanttState, queryGanttStructureVersion, queryGanttTask } from '../gantt-chart.state';
import {
  copyGanttDate,
  GANTT_TASK_ROOT_ID,
  GanttTask,
  GanttTimelineLink,
  GanttTimelineTask,
  getGanttTaskText,
  getGanttTaskType,
} from '../resources/gantt.model';
import { ganttEnsureTaskVisible, ganttValidateLinks } from './dhtmlx-gantt-plugins';

export type GanttChartBusScrollEvent = {
  left: number;
  top: number;
  deltaX: number;
  deltaY: number;
};

@Injectable()
export class GanttChartBusService {
  editable = true;
  afterLinkAdd = new Subject<GanttTimelineLink>();
  afterLinkDelete = new Subject<GanttTimelineLink>();
  afterTaskUpdate$$ = new Subject<GanttTimelineTask>();
  afterTaskUpdate = this.afterTaskUpdate$$.pipe(filter$(() => this.allowAfterTaskUpdate));
  allowAfterLinkSync = true;
  // DHTMLX link deletion is vetoed in onBeforeLinkDelete; this flag lets our own deletes through.
  allowLinkDelete = false;
  afterAutoSchedule = new Subject<GanttTimelineTask[]>();
  scroll = new Subject<GanttChartBusScrollEvent>();
  searchString: string;
  searchString$: Observable<string>;
  _searchString = new BehaviorSubject<string>('');
  gantt: GanttStatic;
  searchVisibleTaskIds: Set<string> | null = null;
  treeVisibleTaskIds: Set<string> | null = null;
  treeDepthByTaskId: Map<string, number> | null = null;
  suppressScrollSync = false;
  private _showBaseDates = false;
  private allowAfterTaskUpdate = true;
  private silentOpDepth = 0;
  private silentOpRaf = 0;

  constructor(
    private ngZone: NgZone,
    private state: State<GanttState>,
  ) {
    this.searchString$ = this._searchString.asObservable();
    this.state.select(queryGanttStructureVersion).pipe(
      skip$(1),
    ).subscribe(() => this.invalidateSearchVisibilityCache());
  }

  get initialized(): boolean {
    return isNotNil(this.gantt);
  }

  setShowBaseDates(val: boolean): void {
    const next = !!val;
    if (this._showBaseDates === next) {
      return;
    }
    this._showBaseDates = next;
    if (!this.gantt) {
      return;
    }
    requestAnimationFrame(() => this.refreshTimelineVisibility());
  }

  get showBaseDates(): boolean {
    return this._showBaseDates;
  }

  setGanttInstance(gantt: GanttStatic): void {
    this.gantt = gantt;
  }

  setTaskProgress(taskId: ID, progress: number): void {
    this.applyTaskProgressBatch([{ taskId, progress }]);
  }

  applyTaskProgressBatch(updates: Array<{ taskId: ID; progress: number }>): void {
    if (!this.gantt || isEmpty(updates)) {
      return;
    }
    this.runOutsideAngularSilently(() => {
      this.gantt.batchUpdate(() => {
        updates.forEach(({ taskId, progress }) => {
          const task = this.gantt.getTask(taskId);
          if (task && task.progress !== progress) {
            task.progress = progress;
            this.gantt.updateTask(taskId);
          }
        });
      });
    });
  }

  setTaskName(taskId: ID, name: string, stateTask: GanttTask): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      task.name = name;
      task.text = getGanttTaskText({ ...stateTask, name });
      this.gantt.updateTask(taskId);

    });
  }

  setTaskEditDialog(taskId: ID, stateTask: GanttTask): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      task.start_date = copyGanttDate(stateTask.startDate);
      task.end_date = copyGanttDate(stateTask.endDate);
      task.duration = stateTask.duration;
      task.progress = stateTask.completionPct;
      task.description = stateTask.description;
      task.text = getGanttTaskText({ ...task, resource: stateTask.resource });
      this.gantt.updateTask(taskId);
      this.gantt.autoSchedule.call(this.gantt, taskId);
    });
  }

  setTaskNote(taskId: ID, note: any): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      task.recentNote = note;
      task.noteMemo = note.memo;
      this.gantt.updateTask(taskId);
    });
  }

  setTaskResource(taskId: ID, resource: any, stateTask: GanttTask): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      task.resource = resource;
      task.text = getGanttTaskText({ ...stateTask, resource });
      this.gantt.updateTask(taskId);
    });
  }

  setTaskDescription(taskId: ID, description: string): void {
    this.runOutsideAngularSilently(() => {
      this.gantt.getTask(taskId).description = description;
      this.gantt.updateTask(taskId);
    });
  }

  setTaskConfirmedWith(taskId: ID, confirmedWith: string): void {
    this.runOutsideAngularSilently(() => {
      this.gantt.getTask(taskId).confirmedWith = confirmedWith;
      this.gantt.updateTask(taskId);
    });
  }

  removeLinks(linkIds: string[]): void {
    this.runOutsideAngularSilently(() => {
      if (linkIds) {
        this.deleteLinksProgrammatically(linkIds);
      }
    });
  }

  deleteLinksProgrammatically(linkIds: Array<string | number>): void {
    this.allowLinkDelete = true;
    try {
      linkIds.forEach((linkId) => {
        if (this.gantt.isLinkExists(linkId)) {
          this.gantt.deleteLink(linkId);
        }
      });
    } finally {
      this.allowLinkDelete = false;
    }
  }

  setTaskExcluded(taskId: ID, excluded: boolean): void {
    this.runOutsideAngularSilently(() => {
      this.gantt.getTask(taskId).isExcludeFromGanttChart = excluded;
      this.gantt.updateTask(taskId);
    });
  }

  setTaskDuration(taskId: ID, duration: number): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      task.duration = duration;
      task.end_date = this.gantt.calculateEndDate(task.start_date, duration, 'hour');
      this.gantt.updateTask(taskId);
      this.gantt.autoSchedule.call(this.gantt, taskId);
    });
  }

  setTaskStartDate(taskId: ID, date: Date): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      task.start_date = date;
      task.end_date = (<any>this.gantt.calculateEndDate)(task);
      this.gantt.updateTask(taskId);
      this.gantt.autoSchedule.call(this.gantt, taskId);
    });
  }

  setTaskConfirmedOnDate(taskId: ID, date: Date): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      task.confirmedOnDate = date;
      this.gantt.updateTask(taskId);
    });
  }

  setTaskConfirmedForDate(taskId: ID, date: Date): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      task.confirmedForDate = date;
      this.gantt.updateTask(taskId);
    });
  }

  setTaskEndDate(taskId: ID, date: Date): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      task.end_date = date;
      task.duration = this.gantt.calculateDuration(task.start_date, task.end_date);
      this.gantt.updateTask(taskId);
      this.gantt.autoSchedule.call(this.gantt, taskId);
    });
  }

  unMarkAsDeletedTask(taskId: ID): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      task.markedAsDeleted = false;
      this.gantt.updateTask(taskId);
    });
  }

  markAsDeletedTask(taskId: ID): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      task.markedAsDeleted = true;
      this.gantt.updateTask(taskId);
      if (task.parent) {
        this.updateTimelineTask(task.parent);
      }
    });
  }

  deleteTask(taskId: ID): void {
    this.runOutsideAngularSilently(() => {
      const task = this.gantt.getTask(taskId);
      this.gantt.deleteTask(task.id);
      if (task.parent) {
        this.updateTimelineTask(task.parent);
      }
    });
  }

  addTask(task: GanttTimelineTask, branchIndex: number = 0): void {
    this.runOutsideAngularSilently(() => {
      this.gantt.addTask(task, <any>task.parent, branchIndex);
      if (task.parent) {
        this.updateTimelineTask(task.parent);
      }
    });
  }

  addTasks(tasks: GanttTimelineTask[], branchIndex: number = 0): void {
    this.runOutsideAngularSilently(() => {
      this.gantt.batchUpdate(() => {
        tasks.forEach((task, i) => {
          // it is supposed that all tasks have the same parent
          this.gantt.addTask(task, <any>task.parent, branchIndex + i);
          if (task.parent) {
            this.updateTimelineTask(task.parent);
          }
        });
      });
    });
  }

  moveTask(
    sourceTaskId: ID,
    branchIndex: number,
    oldDestinationTaskId?: ID,
    newDestinationTaskId?: ID,
  ): void {
    this.runOutsideAngularSilently(() => {
      this.gantt.batchUpdate(() => {
        this.gantt.moveTask(sourceTaskId, branchIndex, newDestinationTaskId);
        if (isNotNil(oldDestinationTaskId)) {
          this.updateTimelineTask(oldDestinationTaskId);
        }
        if (isNotNil(newDestinationTaskId)) {
          this.updateTimelineTask(newDestinationTaskId);
        }
      });
    });
  }

  openTask(taskId: ID): void {
    this.runOutsideAngularSilently(() => {
      this.gantt.open(taskId);
    });
  }

  closeTask(taskId: ID): void {
    this.runOutsideAngularSilently(() => {
      this.gantt.close(taskId);
    });
  }

  openTasks(taskIds: ID[]): void {
    this.runOutsideAngularSilently(() => {
      this.gantt.batchUpdate(() => {
        taskIds.forEach((taskId) => this.gantt.open(taskId));
      });
    });
  }

  closeTasks(taskIds: any[]): void {
    this.runOutsideAngularSilently(() => {
      this.gantt.batchUpdate(() => {
        taskIds.forEach((taskId) => this.gantt.close(taskId));
      });
    });
  }

  replaceLinks(oldLinks: GanttTimelineLink[], newLinks: GanttTimelineLink[]): void {
    this.gantt.batchUpdate(() => {
      const removedLinks = differenceBy(oldLinks, newLinks, 'id');
      this.deleteLinksProgrammatically(removedLinks.map((oldLink) => oldLink.id));
    });
    requestAnimationFrame(() => {
      this.runOutsideAngularSilently(() => {
        this.gantt.batchUpdate(() => {
          const addedLinks = differenceBy(newLinks, oldLinks, 'id');
          const intersectedLinkIds = intersectionBy(oldLinks, newLinks, 'id').map((link) => link.id);

          const oldLinkIndex = keyBy(oldLinks, 'id');
          const normalizedNewLinkIndex = keyBy(newLinks, 'id');
          const updatedLinks = intersectedLinkIds
            .filter((id) => !isEqual(oldLinkIndex[id], normalizedNewLinkIndex[id]))
            .map((id) => normalizedNewLinkIndex[id]);
          addedLinks.forEach((newLink) => this.gantt.addLink(newLink));
          updatedLinks.forEach((updatedLink) => {
            const link = this.gantt.getLink(updatedLink.id);
            assign(link, updatedLink);
            this.gantt.updateLink(String(updatedLink.id));
          });
        });
      });
    });
  }

  showTask(taskId: ID): void {
    this.runOutsideAngularSilently(() => {
      ganttEnsureTaskVisible(this.gantt, taskId);
    });
  }

  revealTaskInTimeline(taskId: ID): void {
    this.runOutsideAngularSilently(() => {
      if (!this.gantt?.getTask?.(taskId)) {
        return;
      }
      this.gantt.showTask(taskId);
      this.gantt.selectTask(taskId);
    });
  }

  scrollVerticallyTo(top: number): void {
    this.runOutsideAngularSilently(() => {
      this.applyVerticalScroll(top);
    });
  }

  scrollVerticallyToSync(top: number): void {
    this.ngZone.runOutsideAngular(() => {
      this.applyVerticalScroll(top);
    });
  }

  private applyVerticalScroll(top: number): void {
    if (!this.gantt) {
      return;
    }
    const scrollState = this.gantt.getScrollState();
    if (Math.abs((scrollState?.y || 0) - top) < 1) {
      return;
    }
    this.gantt.scrollTo(scrollState.x, top);
  }

  refreshTimelineVisibility(): void {
    if (!this.gantt) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      const scrollState = this.gantt.getScrollState();
      this.gantt.refreshData();
      if (scrollState?.x || scrollState?.y) {
        this.gantt.scrollTo(scrollState.x, scrollState.y);
      }
    });
  }

  refreshData(): void {
    if (this.gantt) {
      this.runOutsideAngularSilently(() => {
        this.gantt.batchUpdate(() => {
          this.gantt.render();
        });
      });
    }
  }

  showCompletedTasks(_: boolean): void {
    this.refreshTimelineVisibility();
  }

  showDeletedTasks(_: boolean): void {
    this.refreshTimelineVisibility();
  }

  validateLinks(taskId: ID, links: GanttTimelineLink[]): GanttTimelineLink[] {
    if (isEmpty(links)) {
      return [];
    } else {
      // run basic link validation (self-reference)
      const linksWithSelfReference = links.filter((link) => link.source === link.target);
      if (!isEmpty(linksWithSelfReference)) {
        return linksWithSelfReference;
      }

      // verify that we do not have descendant links
      if (!gantt.config.auto_scheduling_descendant_links) {
        const descendantLinks = links.filter((link) =>
          this.gantt.isChildOf(link.source, link.target) && gantt['isSummaryTask'](
            link.target)
          || this.gantt.isChildOf(
            link.target, link.source) && gantt['isSummaryTask'](link.source));
        if (!isEmpty(descendantLinks)) {
          return descendantLinks;
        }
      }

      // verify that link's source is not repeated
      const repeatedLinks = flatten(toPairs(countBy(links, (link) => link.source))
        .filter(([_, count]) => count > 1)
        .map(([source]) => source)
        .map((source) => filter(links, { source })));

      if (repeatedLinks.length > 0) {
        return repeatedLinks;
      } else {
        // verify that new links do not create circular link
        return ganttValidateLinks(this.gantt, taskId, links);
      }
    }
  }

  getTask(taskId: ID): GanttTimelineTask {
    return this.gantt.getTask(taskId);
  }

  roundDate(date: Date): Date {
    return this.gantt.roundDate(date);
  }

  /**
   * This method tries to find the best time to start task according to the calendar
   */
  correctTaskStartDate(startDate: Date): Date {
    const virtualTask = {
      start_date: startDate,
      duration: 1,
    };
    this.gantt.correctTaskWorkTime(virtualTask);
    return virtualTask.start_date;
  }

  resize(): void {
    this.runOutsideAngularSilently(() => {
      this.gantt.render();
    });
  }

  setFilter(searchString: string): void {
    const normalized = searchString || '';
    if (this.searchString === normalized) {
      return;
    }
    this.searchString = normalized;
    this._searchString.next(normalized);
    this.rebuildSearchVisibilityCache();
  }

  refreshSearchDisplay(): void {
    if (!this.gantt) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.gantt.refreshData();
    });
  }

  rebuildSearchVisibilityCache(): void {
    const searchString = (this.searchString || '').toLowerCase();
    if (!searchString) {
      this.searchVisibleTaskIds = null;
      return;
    }

    const { tasks } = this.state.value;
    const taskIds = Object.keys(tasks);
    const matches = new Set<string>();
    for (let i = 0; i < taskIds.length; i++) {
      const taskId = taskIds[i];
      const task = tasks[taskId];
      if (!task?.taskNumber) {
        continue;
      }
      const numberHit = padStart(String(task.taskNumber), 3, '0').includes(searchString);
      const nameHit = !!task.name && task.name.toLowerCase().includes(searchString);
      const resourceHit = !!task.resource?.name
        && task.resource.name.toLowerCase().includes(searchString);
      if (!numberHit && !nameHit && !resourceHit) {
        continue;
      }
      matches.add(taskId);
      let parentId = task.parentTaskId;
      while (parentId) {
        if (matches.has(parentId)) {
          break;
        }
        matches.add(parentId);
        parentId = tasks[parentId]?.parentTaskId;
      }
    }
    this.searchVisibleTaskIds = matches;
  }

  isTaskVisibleInSearch(taskId: ID): boolean {
    if (!this.searchString) {
      return true;
    }
    if (!this.searchVisibleTaskIds) {
      this.rebuildSearchVisibilityCache();
    }
    return !!this.searchVisibleTaskIds?.has(String(taskId));
  }

  invalidateTreeVisibilityCache(): void {
    this.treeVisibleTaskIds = null;
    this.treeDepthByTaskId = null;
  }

  invalidateSearchVisibilityCache(): void {
    this.searchVisibleTaskIds = null;
  }

  rebuildTreeVisibilityCache(): void {
    const { tasks, tasksByParent } = this.state.value;
    const visible = new Set<string>();
    const depths = new Map<string, number>();
    const stack: Array<{ taskId: string; ancestorsOpened: boolean; depth: number }> = [];
    const rootChildren = tasksByParent[GANTT_TASK_ROOT_ID] || [];
    rootChildren.forEach((taskId) => stack.push({
      taskId: String(taskId),
      ancestorsOpened: true,
      depth: 0,
    }));

    while (stack.length) {
      const { taskId, ancestorsOpened, depth } = stack.pop()!;
      const task = tasks[taskId];
      if (!task) {
        continue;
      }
      depths.set(taskId, depth);
      if (ancestorsOpened) {
        visible.add(taskId);
      }
      const children = tasksByParent[taskId] || [];
      const childAncestorsOpened = ancestorsOpened && !!task.opened;
      children.forEach((childId) => {
        stack.push({
          taskId: String(childId),
          ancestorsOpened: childAncestorsOpened,
          depth: depth + 1,
        });
      });
    }
    this.treeVisibleTaskIds = visible;
    this.treeDepthByTaskId = depths;
  }

  isTaskVisibleInTree(taskId: ID): boolean {
    if (!this.treeVisibleTaskIds) {
      this.rebuildTreeVisibilityCache();
    }
    return !!this.treeVisibleTaskIds?.has(String(taskId));
  }

  getTaskTreeDepth(taskId: ID): number {
    if (!this.treeDepthByTaskId) {
      this.rebuildTreeVisibilityCache();
    }
    return this.treeDepthByTaskId?.get(String(taskId)) ?? 0;
  }

  /**
   * Batch updates for the gantt chart.
   * <b>Note!!!</b> It is better to defer reading of task/project dates until batch is complete,
   * because recalculation is done only when batch is finished
   * @param op
   */
  batchUpdate(op: () => void): void {
    this.gantt.batchUpdate(op);
  }

  private runOutsideAngular(op: () => void): void {
    this.ngZone.runOutsideAngular(op);
  }

  private runOutsideAngularSilently(op: () => void): void {
    if (this.silentOpDepth === 0) {
      this.allowAfterTaskUpdate = false;
      this.suppressScrollSync = true;
    }
    this.silentOpDepth += 1;
    try {
      this.ngZone.runOutsideAngular(op);
    } finally {
      this.silentOpDepth -= 1;
      if (this.silentOpDepth === 0) {
        if (this.silentOpRaf) {
          cancelAnimationFrame(this.silentOpRaf);
        }
        this.silentOpRaf = requestAnimationFrame(() => {
          this.silentOpRaf = 0;
          if (this.silentOpDepth === 0) {
            this.suppressScrollSync = false;
            this.allowAfterTaskUpdate = true;
          }
        });
      }
    }
  }

  private updateTimelineTask(taskId: ID): void {
    const task = this.state.project(queryGanttTask, taskId);
    const timelineTask = this.gantt.getTask(taskId);
    timelineTask.type = getGanttTaskType(task);
    this.gantt.updateTask(taskId);
  }
}
